// Lightweight toast notification system.
//
// Usage from any component / store:
//   import { useToast } from '@/composables/useToast'
//   const toast = useToast()
//   toast.success('Saved')
//   toast.error('Could not connect', { ttl: 0 })   // 0 = manual dismiss only
//   toast.info('Refresh started', { action: { label: 'Undo', onClick: ... } })
//
// The single shared `toasts` ref backs ToastHost.vue, which is mounted once
// in App.vue and renders the visible stack. Components never render toasts
// themselves — they just call useToast() and the host handles layout +
// transitions.
//
// Why a singleton ref instead of Pinia: toasts are pure UI ephemera that
// never need persistence, hydration, or devtools — a 30-line module is
// strictly less surface area than a store.

import { ref, readonly } from 'vue'

const toasts = ref([])
const timers = new Map()
let nextId = 1

function dismiss(id) {
  const t = timers.get(id)
  if (t) {
    clearTimeout(t)
    timers.delete(id)
  }
  toasts.value = toasts.value.filter((t) => t.id !== id)
}

function push(kind, message, options = {}) {
  const id = nextId++
  // Default TTLs: errors stay longer so the user has time to read them;
  // ttl=0 means manual dismiss only (the user must click ✕ or the action).
  const defaultTtl = kind === 'error' ? 7000 : 4500
  const ttl = options.ttl ?? defaultTtl

  const entry = {
    id,
    kind,
    message: String(message ?? ''),
    action: options.action && typeof options.action.onClick === 'function'
      ? { label: String(options.action.label || 'Action'), onClick: options.action.onClick }
      : null,
  }
  toasts.value = [...toasts.value, entry]

  if (ttl > 0) {
    timers.set(id, setTimeout(() => dismiss(id), ttl))
  }
  return id
}

export function useToast() {
  return {
    success: (msg, opts) => push('success', msg, opts),
    error: (msg, opts) => push('error', msg, opts),
    info: (msg, opts) => push('info', msg, opts),
    dismiss,
    toasts: readonly(toasts),
  }
}
